import React, { useState, useEffect } from "react";
import { Modal, Badge, Card, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import {
  X,
  Tag,
  Calendar,
  AlertCircle,
  Clock,
  Paperclip,
  Download,
  MessageCircle,
  Send,
  Edit2,
  Save,
  XCircle,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { getTicketHistory, createTicketHistory, updateTicketHistory, getTicket } from "../../api/apiClient";

// Mapeo de categorías
const CATEGORY_LABELS = {
  0: "tickets.categoryErrors",
  1: "tickets.categoryInquiries",
  2: "tickets.categorySupportRequest",
  3: "tickets.categoryOther",
  4: "tickets.categoryTechnical",
};

// Mapeo de prioridades
const PRIORITY_LABELS = {
  0: "tickets.priorityLow",
  1: "tickets.priorityMedium",
  2: "tickets.priorityHigh",
  3: "tickets.priorityUrgent",
};

// Colores para badges de estado
const STATUS_COLORS = {
  0: "secondary", // Nuevo
  1: "primary", // En Progreso
  2: "success", // Resuelto
  3: "danger", // Cerrado
};

// Colores para badges de prioridad
const PRIORITY_COLORS = {
  0: "info", // Baja
  1: "warning", // Media
  2: "orange", // Alta
  3: "danger", // Urgente
};

const TicketDetailModal = ({ show, onHide, ticket }) => {
  const { t } = useTranslation();

  // Estado para el ticket local (se actualiza cuando se agregan adjuntos)
  const [localTicket, setLocalTicket] = useState(null);

  // Estado para el historial de conversación
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Cargar ticket y historial cuando se abre el modal
  useEffect(() => {
    if (show && ticket) {
      // Cargar siempre el ticket completo desde la API para tener datos actualizados
      loadTicket();
      loadHistory();
    }
  }, [show, ticket]);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await getTicketHistory(ticket.id);
      if (response.success) {
        setHistory(response.data || []);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadTicket = async () => {
    try {
      const response = await getTicket(ticket.id);
      if (response.success) {
        setLocalTicket(response.data);
      }
    } catch (error) {
      console.error("Error loading ticket:", error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    const hadFiles = commentFiles.length > 0;
    try {
      const userEmail = sessionStorage.getItem("userEmail");
      const userId = sessionStorage.getItem("idUser");

      const historyData = {
        userId: parseInt(userId, 10),
        userName: userEmail,
        commentText: newComment,
        isAdmin: false, // Usuario final
      };

      const response = await createTicketHistory(ticket.id, historyData, commentFiles);

      if (response.success) {
        setNewComment("");
        setCommentFiles([]);
        await loadHistory(); // Recargar historial

        // Si se agregaron archivos, recargar el ticket completo para actualizar la lista de adjuntos
        if (hadFiles) {
          await loadTicket();
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = (commentId, currentText) => {
    setEditingCommentId(commentId);
    setEditText(currentText);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim()) return;

    setSubmitting(true);
    try {
      const response = await updateTicketHistory(editingCommentId, editText);

      if (response.success) {
        setEditingCommentId(null);
        setEditText("");
        await loadHistory(); // Recargar historial
      }
    } catch (error) {
      console.error("Error updating comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditText("");
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + commentFiles.length > 5) {
      alert(t("tickets.maxFilesExceeded"));
      return;
    }
    setCommentFiles([...commentFiles, ...files]);
  };

  const handleRemoveFile = (index) => {
    setCommentFiles(commentFiles.filter((_, i) => i !== index));
  };

  if (!localTicket) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(2)} MB`;
    const kb = bytes / 1024;
    return `${kb.toFixed(2)} KB`;
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      backdrop="static"
      keyboard={true}
      contentClassName="ticket-detail-modal"
    >
      <Modal.Header
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          border: "none",
          borderRadius: "15px 15px 0 0",
        }}
      >
        <Modal.Title className="text-white fw-bold d-flex align-items-center gap-2">
          <AlertCircle size={24} />
          {t("tickets.detailTitle")}
        </Modal.Title>
        <button
          onClick={onHide}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "none",
            borderRadius: "8px",
            padding: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
          }}
        >
          <X size={20} />
        </button>
      </Modal.Header>

      <Modal.Body
        style={{
          background: "rgba(245, 247, 250, 1)",
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {/* Ticket Number y Título */}
        <div className="mb-3">
          <span
            className="badge bg-dark mb-2"
            style={{ fontSize: "0.8rem", fontFamily: "monospace" }}
          >
            {localTicket.ticketNumber}
          </span>
          <h5 className="fw-bold mb-0" style={{ color: "#1e293b" }}>
            {localTicket.title}
          </h5>
        </div>

        {/* Badges de estado y prioridad */}
        <div className="d-flex gap-2 mb-4">
          <Badge bg={STATUS_COLORS[localTicket.status]}>
            {t(`tickets.status${localTicket.status}`)}
          </Badge>
          <Badge
            bg={PRIORITY_COLORS[localTicket.priority]}
            style={
              localTicket.priority === 2
                ? { backgroundColor: "#ff9800", color: "white" }
                : {}
            }
          >
            {t(PRIORITY_LABELS[localTicket.priority])}
          </Badge>
        </div>

        {/* Información del ticket */}
        <Row className="g-3 mb-4">
          <Col xs={12} md={6}>
            <Card
              className="border-0 shadow-sm"
              style={{ borderRadius: "12px" }}
            >
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Tag size={16} style={{ color: "#667eea" }} />
                  <small
                    className="text-uppercase fw-semibold"
                    style={{ color: "#64748b", fontSize: "0.7rem" }}
                  >
                    {t("tickets.category")}
                  </small>
                </div>
                <p className="mb-0 fw-medium" style={{ color: "#1e293b" }}>
                  {t(CATEGORY_LABELS[localTicket.category] || "tickets.categoryOther")}
                </p>
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} md={6}>
            <Card
              className="border-0 shadow-sm"
              style={{ borderRadius: "12px" }}
            >
              <Card.Body className="p-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <Calendar size={16} style={{ color: "#667eea" }} />
                  <small
                    className="text-uppercase fw-semibold"
                    style={{ color: "#64748b", fontSize: "0.7rem" }}
                  >
                    {t("tickets.createdAt")}
                  </small>
                </div>
                <p className="mb-0 fw-medium" style={{ color: "#1e293b" }}>
                  {formatDate(localTicket.createdAt)}
                </p>
              </Card.Body>
            </Card>
          </Col>

          {localTicket.updatedAt && localTicket.updatedAt !== localTicket.createdAt && (
            <Col xs={12} md={6}>
              <Card
                className="border-0 shadow-sm"
                style={{ borderRadius: "12px" }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Clock size={16} style={{ color: "#667eea" }} />
                    <small
                      className="text-uppercase fw-semibold"
                      style={{ color: "#64748b", fontSize: "0.7rem" }}
                    >
                      {t("tickets.updatedAt")}
                    </small>
                  </div>
                  <p className="mb-0 fw-medium" style={{ color: "#1e293b" }}>
                    {formatDate(localTicket.updatedAt)}
                  </p>
                </Card.Body>
              </Card>
            </Col>
          )}

        </Row>

        {/* Descripción */}
        <Card
          className="border-0 shadow-sm mb-4"
          style={{ borderRadius: "12px" }}
        >
          <Card.Body className="p-3">
            <h6
              className="text-uppercase fw-semibold mb-3"
              style={{ color: "#64748b", fontSize: "0.75rem" }}
            >
              {t("tickets.description")}
            </h6>
            <p
              style={{
                color: "#334155",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
              }}
            >
              {localTicket.description}
            </p>
          </Card.Body>
        </Card>

        {/* Archivos adjuntos */}
        {localTicket.attachments && localTicket.attachments.length > 0 && (
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ borderRadius: "12px" }}
          >
            <Card.Body className="p-3">
              <div className="d-flex align-items-center gap-2 mb-3">
                <Paperclip size={16} style={{ color: "#667eea" }} />
                <h6
                  className="text-uppercase fw-semibold mb-0"
                  style={{ color: "#64748b", fontSize: "0.75rem" }}
                >
                  {t("tickets.attachments")} ({localTicket.attachments.length})
                </h6>
              </div>

              <div className="d-flex flex-column gap-2">
                {localTicket.attachments.map((attachment) => (
                  <a
                    key={attachment.id}
                    href={attachment.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-decoration-none"
                  >
                    <div
                      className="d-flex align-items-center justify-content-between p-2 rounded"
                      style={{
                        background: "rgba(102, 126, 234, 0.1)",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(102, 126, 234, 0.2)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background =
                          "rgba(102, 126, 234, 0.1)")
                      }
                    >
                      <div className="d-flex align-items-center gap-2 flex-grow-1">
                        <Paperclip size={16} style={{ color: "#667eea" }} />
                        <div>
                          <p
                            className="mb-0 fw-medium"
                            style={{ color: "#1e293b", fontSize: "0.9rem" }}
                          >
                            {attachment.fileName}
                          </p>
                          <small style={{ color: "#64748b" }}>
                            {attachment.fileType} • {formatFileSize(attachment.fileSize)}
                          </small>
                        </div>
                      </div>
                      <Download size={16} style={{ color: "#667eea" }} />
                    </div>
                  </a>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Conversación / Historial */}
        <Card
          className="border-0 shadow-sm"
          style={{ borderRadius: "12px" }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center gap-2 mb-3">
              <MessageCircle size={16} style={{ color: "#667eea" }} />
              <h6
                className="text-uppercase fw-semibold mb-0"
                style={{ color: "#64748b", fontSize: "0.75rem" }}
              >
                {t("tickets.conversation")}
              </h6>
            </div>

            {/* Historial de comentarios */}
            {loadingHistory ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" style={{ color: "#667eea" }} />
              </div>
            ) : (
              <div className="d-flex flex-column gap-3 mb-4" style={{ maxHeight: "400px", overflowY: "auto" }}>
                {history.length === 0 ? (
                  <p className="text-center text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                    {t("tickets.noComments")}
                  </p>
                ) : (
                  history.map((comment) => {
                    const isUserComment = comment.userId === parseInt(sessionStorage.getItem("idUser"), 10);
                    const isEditing = editingCommentId === comment.id;

                    return (
                      <div
                        key={comment.id}
                        className="p-3 rounded"
                        style={{
                          background: comment.isAdmin
                            ? "rgba(102, 126, 234, 0.08)"
                            : "rgba(249, 250, 251, 1)",
                          border: comment.isAdmin
                            ? "1px solid rgba(102, 126, 234, 0.2)"
                            : "1px solid rgba(226, 232, 240, 1)",
                        }}
                      >
                        {/* Header del comentario */}
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <User size={16} style={{ color: comment.isAdmin ? "#667eea" : "#64748b" }} />
                            <span className="fw-semibold" style={{ color: "#1e293b", fontSize: "0.9rem" }}>
                              {comment.isAdmin ? "Admin" : comment.userName}
                            </span>
                            {comment.isAdmin && (
                              <Badge bg="primary" style={{ fontSize: "0.7rem" }}>
                                {t("tickets.adminBadge")}
                              </Badge>
                            )}
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <small style={{ color: "#64748b" }}>
                              {formatDate(comment.createdAt)}
                            </small>
                            {comment.isEdited && (
                              <small style={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                                ({t("tickets.edited")})
                              </small>
                            )}
                          </div>
                        </div>

                        {/* Texto del comentario */}
                        {isEditing ? (
                          <div className="mb-2">
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              style={{
                                borderRadius: "8px",
                                border: "1px solid #cbd5e1",
                                fontSize: "0.9rem",
                              }}
                            />
                            <div className="d-flex gap-2 mt-2">
                              <Button
                                size="sm"
                                onClick={handleSaveEdit}
                                disabled={submitting || !editText.trim()}
                                style={{
                                  background: "#667eea",
                                  border: "none",
                                  borderRadius: "6px",
                                }}
                              >
                                <Save size={14} className="me-1" />
                                {t("tickets.saveButton")}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={handleCancelEdit}
                                disabled={submitting}
                                style={{
                                  borderRadius: "6px",
                                }}
                              >
                                <XCircle size={14} className="me-1" />
                                {t("tickets.cancelButton")}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p
                              className="mb-2"
                              style={{
                                color: "#334155",
                                fontSize: "0.9rem",
                                lineHeight: "1.6",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {comment.commentText}
                            </p>
                            {isUserComment && !comment.isAdmin && (
                              <Button
                                size="sm"
                                variant="link"
                                onClick={() => handleEditComment(comment.id, comment.commentText)}
                                style={{
                                  color: "#667eea",
                                  textDecoration: "none",
                                  padding: 0,
                                  fontSize: "0.85rem",
                                }}
                              >
                                <Edit2 size={12} className="me-1" />
                                {t("tickets.editButton")}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* Formulario para nuevo comentario */}
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid #e2e8f0" }}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: "#64748b", fontSize: "0.85rem" }}>
                  {t("tickets.addComment")}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder={t("tickets.addCommentPlaceholder")}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  style={{
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    fontSize: "0.9rem",
                  }}
                />
              </Form.Group>

              {/* Selector de archivos */}
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: "#64748b", fontSize: "0.85rem" }}>
                  {t("tickets.attachFiles")} ({t("tickets.optional")})
                </Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                  style={{
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    fontSize: "0.9rem",
                  }}
                />
                <Form.Text className="text-muted" style={{ fontSize: "0.75rem" }}>
                  {t("tickets.maxFiles")}
                </Form.Text>
              </Form.Group>

              {/* Vista previa de archivos seleccionados */}
              {commentFiles.length > 0 && (
                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    {commentFiles.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                        style={{
                          background: "rgba(102, 126, 234, 0.1)",
                          fontSize: "0.85rem",
                        }}
                      >
                        <Paperclip size={12} style={{ color: "#667eea" }} />
                        <span style={{ color: "#1e293b" }}>{file.name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <XCircle size={14} style={{ color: "#ef4444" }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botón de enviar */}
              <Button
                onClick={handleAddComment}
                disabled={submitting || !newComment.trim()}
                style={{
                  background: "#667eea",
                  border: "none",
                  borderRadius: "10px",
                  padding: "8px 20px",
                  fontWeight: 600,
                  width: "100%",
                }}
              >
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {t("tickets.sending")}
                  </>
                ) : (
                  <>
                    <Send size={16} className="me-2" />
                    {t("tickets.addCommentButton")}
                  </>
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Modal.Body>

      <Modal.Footer
        style={{
          background: "white",
          border: "none",
          borderRadius: "0 0 15px 15px",
        }}
      >
        <button
          onClick={onHide}
          style={{
            background: "#667eea",
            border: "none",
            borderRadius: "10px",
            padding: "10px 24px",
            color: "white",
            fontWeight: 600,
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.background = "#5568d3")}
          onMouseLeave={(e) => (e.target.style.background = "#667eea")}
        >
          {t("tickets.closeButton")}
        </button>
      </Modal.Footer>

      {/* Estilos para corregir las esquinas blancas del modal */}
      <style>{`
        .ticket-detail-modal {
          border-radius: 15px !important;
          overflow: hidden !important;
          border: none !important;
        }
      `}</style>
    </Modal>
  );
};

export default TicketDetailModal;
